import { SeriesWeekday } from '@app/shared/types/models/reports/time-report-series';
import { TimePickerValue } from '@app/shared/types/models/shared/time-picker-value';
import { convertHourToIsoTime } from './time-utils';

/** A series may span at most this many calendar days (inclusive). Intentional product limit. */
export const MAX_SERIES_CALENDAR_DAYS = 31;
export const SERIES_BREAK_PRESETS = [10, 15, 30] as const;
export const DEFAULT_SERIES_BREAK_MINUTES = 30;
export const BREAK_START_OFFSET_MINUTES = 210;
export const BREAK_SUGGESTION_MIN_RANGE_MINUTES = 4 * 60;
export const MAX_SERIES_BREAK_MINUTES = 999;

export const DEFAULT_SERIES_WEEKDAYS: readonly SeriesWeekday[] = [
  SeriesWeekday.Monday,
  SeriesWeekday.Tuesday,
  SeriesWeekday.Wednesday,
  SeriesWeekday.Thursday,
  SeriesWeekday.Friday
];

export interface SeriesWeekdayOption {
  value: SeriesWeekday;
  label: string;
  shortLabel: string;
}

export const SERIES_WEEKDAY_OPTIONS: readonly SeriesWeekdayOption[] = [
  ['monday', SeriesWeekday.Monday],
  ['tuesday', SeriesWeekday.Tuesday],
  ['wednesday', SeriesWeekday.Wednesday],
  ['thursday', SeriesWeekday.Thursday],
  ['friday', SeriesWeekday.Friday],
  ['saturday', SeriesWeekday.Saturday],
  ['sunday', SeriesWeekday.Sunday]
].map(([key, value]) => ({
  value: value as SeriesWeekday,
  label: `time-reports.series.weekdays.${key}`,
  shortLabel: `time-reports.series.weekdays.${key}-short`
}));

/** Validation outcome shared by every dialog that offers a series. */
export type SeriesErrorKind =
  | 'seriesRangeRequired'
  | 'seriesInvalidRange'
  | 'seriesTooLong'
  | 'seriesWeekdaysRequired'
  | 'seriesNoDays'
  | 'seriesInvalidDailyRange'
  | 'seriesBreakOutsideShift';

export interface SeriesValidationInput {
  dateFrom: Date | null | undefined;
  dateTo: Date | null | undefined;
  weekdays: readonly SeriesWeekday[];
  /** Daily from/to in Range mode; `undefined` in Manual mode. */
  dailyRange?: { from: TimePickerValue; to: TimePickerValue };
  /** Deducted break in Range mode; 0 means no break. */
  breakMinutes?: number;
}

export function toLocalIsoDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function toClockValue(value: TimePickerValue): string {
  return `${String(value.hours).padStart(2, '0')}:${String(value.minutes).padStart(2, '0')}:00`;
}

export function timePickerToMinutes(value: TimePickerValue | null | undefined): number | undefined {
  if (!value || value.hours < 0 || value.hours > 23 || value.minutes < 0 || value.minutes > 59) {
    return undefined;
  }
  return value.hours * 60 + value.minutes;
}

/**
 * Gross minutes of a same-day daily range. Returns `undefined` unless start is strictly before end,
 * matching the single-report Range rule (no overnight ranges).
 */
export function dailyRangeMinutes(
  from: TimePickerValue | null | undefined,
  to: TimePickerValue | null | undefined
): number | undefined {
  const fromMinutes = timePickerToMinutes(from);
  const toMinutes = timePickerToMinutes(to);
  if (fromMinutes === undefined || toMinutes === undefined || toMinutes <= fromMinutes) {
    return undefined;
  }
  return toMinutes - fromMinutes;
}

export function minutesToIsoDuration(minutes: number): string | undefined {
  if (!Number.isInteger(minutes) || minutes <= 0) {
    return undefined;
  }
  return convertHourToIsoTime(Math.floor(minutes / 60), minutes % 60);
}

export function includedWeekdayMask(values: readonly SeriesWeekday[]): number {
  return values.reduce((mask, weekday) => mask | weekday, 0);
}

export function weekdayForLocalDate(value: Date): SeriesWeekday {
  const day = value.getDay();
  return day === 0 ? SeriesWeekday.Sunday : 1 << (day - 1);
}

/** Counts calendar days without being affected by daylight-saving transitions. */
export function inclusiveLocalDayCount(from: Date, to: Date): number | undefined {
  const fromUtc = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const toUtc = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());

  if (toUtc < fromUtc) {
    return undefined;
  }

  return Math.round((toUtc - fromUtc) / 86_400_000) + 1;
}

/** Local calendar days (midnight) in the inclusive range whose weekday is selected. */
export function seriesOccurrenceDates(
  from: Date | null | undefined,
  to: Date | null | undefined,
  weekdays: readonly SeriesWeekday[]
): Date[] {
  if (!from || !to || weekdays.length === 0) {
    return [];
  }

  const count = inclusiveLocalDayCount(from, to);
  if (!count || count > MAX_SERIES_CALENDAR_DAYS) {
    return [];
  }

  const mask = includedWeekdayMask(weekdays);
  const dates: Date[] = [];
  for (let index = 0; index < count; index += 1) {
    const day = new Date(from.getFullYear(), from.getMonth(), from.getDate() + index);
    if ((mask & weekdayForLocalDate(day)) !== 0) {
      dates.push(day);
    }
  }
  return dates;
}

export function countSeriesOccurrenceDays(
  from: Date | null | undefined,
  to: Date | null | undefined,
  weekdays: readonly SeriesWeekday[]
): number {
  return seriesOccurrenceDates(from, to, weekdays).length;
}

/** True when the series resolves to exactly one day and must use the regular single-report POST. */
export function isSingleDaySeries(dates: readonly Date[]): boolean {
  return dates.length === 1;
}

export function addClockMinutes(value: TimePickerValue, minutes: number): TimePickerValue {
  const totalMinutes = (value.hours * 60 + value.minutes + minutes) % (24 * 60);
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

export function shouldSuggestBreakStart(grossMinutes: number | undefined): boolean {
  return grossMinutes !== undefined && grossMinutes > BREAK_SUGGESTION_MIN_RANGE_MINUTES;
}

export function suggestedBreakStart(
  from: TimePickerValue,
  grossMinutes: number | undefined
): TimePickerValue | undefined {
  if (!shouldSuggestBreakStart(grossMinutes)) {
    return undefined;
  }

  return addClockMinutes(from, BREAK_START_OFFSET_MINUTES);
}

export function isBreakWithinShift(grossMinutes: number | undefined, breakMinutes: number): boolean {
  if (!breakMinutes) {
    return true;
  }

  if (grossMinutes === undefined || breakMinutes < 0 || breakMinutes >= grossMinutes) {
    return false;
  }

  if (!shouldSuggestBreakStart(grossMinutes)) {
    return true;
  }

  return BREAK_START_OFFSET_MINUTES + breakMinutes <= grossMinutes;
}

/** Single validation rule set for series mode, used by both report dialogs. */
export function validateSeriesInput(input: SeriesValidationInput): SeriesErrorKind | undefined {
  const { dateFrom, dateTo, weekdays, dailyRange, breakMinutes = 0 } = input;
  if (!dateFrom || !dateTo) {
    return 'seriesRangeRequired';
  }

  const dayCount = inclusiveLocalDayCount(dateFrom, dateTo);
  if (dayCount === undefined) {
    return 'seriesInvalidRange';
  }
  if (dayCount > MAX_SERIES_CALENDAR_DAYS) {
    return 'seriesTooLong';
  }
  if (weekdays.length === 0) {
    return 'seriesWeekdaysRequired';
  }
  if (seriesOccurrenceDates(dateFrom, dateTo, weekdays).length === 0) {
    return 'seriesNoDays';
  }

  if (dailyRange) {
    const grossMinutes = dailyRangeMinutes(dailyRange.from, dailyRange.to);
    if (grossMinutes === undefined) {
      return 'seriesInvalidDailyRange';
    }
    if (!isBreakWithinShift(grossMinutes, breakMinutes)) {
      return 'seriesBreakOutsideShift';
    }
  }

  return undefined;
}

/** Browser IANA time zone used to resolve series wall-clock values. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
